import React, { useState, useEffect } from 'react';
import { CalendarCheck, KanbanSquare, LayoutDashboard, DollarSign, ClipboardList, Command, Zap, Sparkles, Activity } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { KanbanBoard } from './kanban/KanbanBoard.tsx';
import { CalendarView } from './calendar/CalendarView.tsx';
import { BudgetView } from './budget/BudgetView.tsx';
import { DashboardView } from './dashboard/DashboardView.tsx';
import { WeddingPlanSummary } from './weddingPlan/WeddingPlanSummary.tsx';
import Navbar from '../../components/Navbar.tsx';
import { useAgentStatus, globalRefreshEmitter } from '../../hooks/useAgentIntegration.ts';
import { useChatStore } from '../../components/chat/chatStore.ts';
import { useChatbot } from '../../components/chat/GlobalChatbot.tsx';

type PlanningTool = 'dashboard' | 'task-board' | 'calendar' | 'budget' | 'wedding-plan';

const PlanPage: React.FC = () => {
  const [selectedTool, setSelectedTool] = useState<PlanningTool>('wedding-plan');
  const [mobileToolIndex, setMobileToolIndex] = useState(0);
  
  // Component-specific refresh keys for granular updates
  const [componentRefreshKeys, setComponentRefreshKeys] = useState({
    dashboard: 0,
    'task-board': 0,
    calendar: 0,
    budget: 0,
    'wedding-plan': 0
  });

  // Agent integration
  const { isAgentActive, lastActionTime } = useAgentStatus();
  const { pendingActions } = useChatStore();
  
  // Chatbot state for positioning adjustment
  const { isOpen: isChatOpen } = useChatbot();

  // Listen for specific component refresh events
  useEffect(() => {
    const handleComponentRefresh = (event: CustomEvent) => {
      const { componentType, action } = event.detail;
      
      console.log('Component refresh event:', componentType, action);
      
      // Only refresh specific components based on action type
      if (componentType === 'budget' || (action && action.type === 'budget')) {
        setComponentRefreshKeys(prev => ({
          ...prev,
          budget: prev.budget + 1,
          dashboard: prev.dashboard + 1 // Dashboard shows budget summary, so refresh it too
        }));
      } else if (componentType === 'calendar' || (action && action.type === 'calendar')) {
        setComponentRefreshKeys(prev => ({
          ...prev,
          calendar: prev.calendar + 1,
          dashboard: prev.dashboard + 1
        }));
      } else if (componentType === 'task' || (action && action.type === 'task')) {
        setComponentRefreshKeys(prev => ({
          ...prev,
          'task-board': prev['task-board'] + 1,
          calendar: prev.calendar + 1, // Calendar shows tasks with due dates
          dashboard: prev.dashboard + 1
        }));
      } else if (componentType === 'global') {
        // Only refresh all components for global refresh events
        setComponentRefreshKeys(prev => ({
          dashboard: prev.dashboard + 1,
          'task-board': prev['task-board'] + 1,
          calendar: prev.calendar + 1,
          budget: prev.budget + 1,
          'wedding-plan': prev['wedding-plan'] + 1
        }));
      }
    };

    globalRefreshEmitter.addEventListener('refresh', handleComponentRefresh as EventListener);

    return () => {
      globalRefreshEmitter.removeEventListener('refresh', handleComponentRefresh as EventListener);
    };
  }, []);



  // Handle URL parameters for navigation
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && ['dashboard', 'task-board', 'calendar', 'budget', 'wedding-plan'].includes(tab)) {
      setSelectedTool(tab as PlanningTool);
      const toolIndex = tools.findIndex(tool => tool.id === tab);
      if (toolIndex !== -1) {
        setMobileToolIndex(toolIndex);
      }
    }
  }, []);

  const tools = [
    { id: 'wedding-plan', label: 'Overview', icon: ClipboardList, shortcut: '⌘1' },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, shortcut: '⌘2' },
    { id: 'task-board', label: 'Tasks', icon: KanbanSquare, shortcut: '⌘3' },
    { id: 'calendar', label: 'Calendar', icon: CalendarCheck, shortcut: '⌘4' },
    { id: 'budget', label: 'Budget', icon: DollarSign, shortcut: '⌘5' },
  ];

  const handleToolChange = (toolId: PlanningTool, index: number) => {
    setSelectedTool(toolId);
    setMobileToolIndex(index);
    
    // Update URL
    const params = new URLSearchParams(window.location.search);
    params.set('tab', toolId);
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
  };

  const handleMobileNavigation = (direction: 'next' | 'prev') => {
    const newIndex = direction === 'next'
      ? (mobileToolIndex + 1) % tools.length
      : (mobileToolIndex - 1 + tools.length) % tools.length;
    
    setMobileToolIndex(newIndex);
    setSelectedTool(tools[newIndex].id as PlanningTool);
    
    // Update URL
    const params = new URLSearchParams(window.location.search);
    params.set('tab', tools[newIndex].id);
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
  };

  const getToolActionCount = (toolId: string) => {
    return pendingActions.filter(action => {
      switch (toolId) {
        case 'calendar':
          return action.type === 'calendar';
        case 'task-board':
          return action.type === 'task';
        case 'budget':
          return action.type === 'budget';
        default:
          return false;
      }
    }).length;
  };

  const renderToolContent = () => {
    // Use component-specific refresh keys for granular updates
    switch (selectedTool) {
      case 'dashboard':
        return <DashboardView key={`dashboard-${componentRefreshKeys.dashboard}`} onNavigate={setSelectedTool} />;
      case 'task-board':
        return <KanbanBoard key={`kanban-${componentRefreshKeys['task-board']}`} />;
      case 'calendar':
        return <CalendarView key={`calendar-${componentRefreshKeys.calendar}`} />;
      case 'budget':
        return <BudgetView key={`budget-${componentRefreshKeys.budget}`} />;
      case 'wedding-plan':
        return <WeddingPlanSummary key={`wedding-plan-${componentRefreshKeys['wedding-plan']}`} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Navbar />
      
      {/* Agent Status Indicator - Modern style */}
      <AnimatePresence>
        {(isAgentActive || pendingActions.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-20 right-6 z-50 bg-gray-900/95 backdrop-blur-xl border border-gray-700/50 text-white px-4 py-2.5 rounded-xl shadow-2xl"
          >
            <div className="flex items-center space-x-2.5">
              <motion.div
                animate={{ rotate: isAgentActive ? 360 : 0 }}
                transition={{ duration: 1, repeat: isAgentActive ? Infinity : 0, ease: "linear" }}
                className="p-1.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg"
              >
                <Sparkles className="w-3.5 h-3.5" />
              </motion.div>
              <div className="flex flex-col">
              <span className="text-sm font-medium">
                  {isAgentActive ? 'AI Agent Working...' : 'Actions Completed'}
                </span>
                <span className="text-xs text-gray-400">
                  {isAgentActive ? 'Processing your request' : `${pendingActions.length} pending`}
              </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="min-h-screen ">
        {/* Hovering Pill Navigation - Desktop */}
        <div className={`hidden md:block fixed top-32 z-40 transform -translate-x-1/2 transition-all duration-300 ${
          isChatOpen ? 'left-[calc(50%+210px)]' : 'left-1/2'
        }`}>
          <motion.nav 
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="flex items-center bg-white/80 backdrop-blur-xl rounded-full p-1.5 shadow-2xl border border-white/20 hover:shadow-3xl transition-shadow duration-300"
            whileHover={{ scale: 1.02 }}
          >
            {tools.map((tool, index) => {
              const actionCount = getToolActionCount(tool.id);
              const isActive = selectedTool === tool.id;
              
              return (
                <motion.button
                  key={tool.id}
                  onClick={() => handleToolChange(tool.id as PlanningTool, index)}
                  className={`relative px-4 py-2.5 mx-0.5 rounded-full font-medium text-sm transition-all duration-300 ${
                    isActive 
                      ? 'text-white shadow-lg' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                    }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {/* Active background with gradient */}
                  {isActive && (
                    <motion.div
                      layoutId="activeTabPill"
                      className="absolute inset-0 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full shadow-lg"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    />
                  )}
                  
                  {/* Content */}
                  <span className="relative z-10">{tool.label}</span>
                  
                  {/* Action count badge */}
                  {actionCount > 0 && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-md"
                    >
                      {actionCount}
                    </motion.div>
                  )}
                  
                  {/* Hover glow effect */}
                  {!isActive && (
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-500/0 to-rose-500/0 hover:from-pink-500/10 hover:to-rose-500/10 transition-all duration-300" />
                  )}
                </motion.button>
              );
            })}
          </motion.nav>
        </div>
        
        <div className="flex h-[calc(100vh-8rem)]">

          {/* Mobile Navigation */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200/60 backdrop-blur-xl">
            <div className="flex justify-around py-2">
              {tools.map((tool, index) => {
                const actionCount = getToolActionCount(tool.id);
                const isActive = index === mobileToolIndex;
                
                return (
                  <button
                    key={tool.id}
                    onClick={() => handleToolChange(tool.id as PlanningTool, index)}
                    className={`flex flex-col items-center py-2 px-3 rounded-lg transition-all duration-200 ${
                      isActive 
                        ? 'text-blue-600' 
                        : 'text-gray-400'
                    }`}
                  >
                    <div className="relative">
                      <tool.icon className="w-5 h-5" />
                      {actionCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-medium">
                          {actionCount}
                        </span>
                      )}
                    </div>
                    <span className="text-xs mt-1 font-medium">{tool.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            
            {/* Content */}
            <div className="flex-1 overflow-auto bg-gray-50/30 p-6">
       
            <motion.div 
                  key={selectedTool}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="h-full"
                >
                  {renderToolContent()}
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      
    </>
  );
};

export default PlanPage;